import {
  ISSUABLE_TYPES,
  LICENSE_TYPES,
  PRODUCTS,
  productName,
  quotaBucket,
} from "../config";

export const EXPIRING_WINDOW_MONTHS = 3;

export const STATUS_META = {
  available: { label: "Available", hint: "Unassigned stock" },
  issued: { label: "Issued", hint: "Sent to a customer, not activated yet" },
  active: { label: "Activated", hint: "Activated on a device" },
  expiring: { label: "Expiring soon", hint: "Expires within 3 months" },
  expired: { label: "Expired", hint: "Past its expiry date" },
};

export const STATUS_ORDER = [
  "available",
  "issued",
  "active",
  "expiring",
  "expired",
];

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  // expirydate is stored as "YYYY-MM-DD"; read it as a local calendar date
  // so it doesn't shift by a day west of UTC.
  const ymd =
    typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export function formatDate(value) {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Display status of a licence. The stored `status` field stays "free" /
// "used" because devices rely on it; everything else is derived. Cloud
// Functions store the result as `displayStatus` (used for filters and
// counts); it is preferred so badges always agree with the counts.
export function licenseStatus(license, now = new Date()) {
  if (STATUS_META[license.displayStatus]) return license.displayStatus;
  return computeLicenseStatus(license, now);
}

export function computeLicenseStatus(license, now = new Date()) {
  const expiry = toDate(license.expirydate);
  if (expiry) {
    if (expiry < now) return "expired";
    const soon = new Date(now);
    soon.setMonth(soon.getMonth() + EXPIRING_WINDOW_MONTHS);
    if (expiry <= soon) return "expiring";
  }
  if (license.status === "used") return "active";
  return license.distributorId || license.issuedAt ? "issued" : "available";
}

// Development / internal keys (e.g. the team's own test devices) stay usable
// but are left out of counts and trends.
export const isDevelopment = (license) => license.development === true;

export function countByStatus(licenses, now = new Date()) {
  const counts = Object.fromEntries(STATUS_ORDER.map((s) => [s, 0]));
  licenses.forEach((license) => {
    counts[licenseStatus(license, now)] += 1;
  });
  return counts;
}

export function compareBy(field, direction) {
  const sign = direction === "asc" ? 1 : -1;
  return (a, b) => {
    const av = sortValue(a[field]);
    const bv = sortValue(b[field]);
    // Missing values always sort last.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    return 0;
  };
}

function sortValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") return value.toUpperCase();
  const date = toDate(value);
  return date ? date.getTime() : value;
}

export function generateLicenseKey() {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
  return hex.match(/.{4}/g).join("-");
}

// Every quota bucket that has been used for this distributor.
export function quotaEntries(distributor, { includeEmpty = false } = {}) {
  const quota = distributor?.quota || {};
  const entries = [];
  PRODUCTS.forEach((product) => {
    LICENSE_TYPES.forEach((type) => {
      const bucket = quotaBucket(product.id, type);
      const allocated = quota[bucket]?.allocated || 0;
      const issued = quota[bucket]?.issued || 0;
      const used = allocated > 0 || issued > 0;
      if (!used && !(includeEmpty && ISSUABLE_TYPES.includes(type))) return;
      entries.push({
        bucket,
        productId: product.id,
        productName: product.name,
        type,
        allocated,
        issued,
        remaining: Math.max(allocated - issued, 0),
      });
    });
  });
  return entries;
}

export function quotaTotals(distributor) {
  return quotaEntries(distributor).reduce(
    (totals, entry) => ({
      allocated: totals.allocated + entry.allocated,
      issued: totals.issued + entry.issued,
      remaining: totals.remaining + entry.remaining,
    }),
    { allocated: 0, issued: 0, remaining: 0 },
  );
}

export const bucketLabel = (productId, type) =>
  `${productName(productId)} · ${type}`;

// Cells that start with these characters are treated as formulas by Excel
// and Sheets; customer names are typed by distributors, so neutralise them.
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

function csvCell(value) {
  let text = value === null || value === undefined ? "" : String(value);
  if (FORMULA_PREFIX.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(rows, columns) {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const body = rows.map((row) =>
    columns.map((c) => csvCell(c.value(row))).join(","),
  );
  return [header, ...body].join("\r\n");
}

export function downloadCsv(filename, csv) {
  // BOM so Excel opens UTF-8 (accented customer names) correctly.
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const LICENSE_CSV_COLUMNS = [
  { label: "Key", value: (l) => l.licenseKey || l.id },
  { label: "Product", value: (l) => productName(l.product) },
  { label: "Type", value: (l) => l.type },
  { label: "Status", value: (l) => STATUS_META[licenseStatus(l)].label },
  { label: "Distributor", value: (l) => l.distributor },
  { label: "Region", value: (l) => l.region },
  { label: "Customer", value: (l) => l.customer },
  { label: "Customer email", value: (l) => l.customerEmail },
  { label: "Location", value: (l) => l.location },
  { label: "Remarks", value: (l) => l.remarks },
  { label: "Created", value: (l) => formatDate(l.createdOn) },
  { label: "Issued", value: (l) => formatDate(l.issuedAt) },
  { label: "Activated", value: (l) => formatDate(l.registeredDate) },
  { label: "Expiry", value: (l) => l.expirydate || "" },
  { label: "Development", value: (l) => (isDevelopment(l) ? "Yes" : "") },
];

export function todayStamp() {
  return new Date().toISOString().slice(0, 10);
}
